import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { FiBookmark, FiChevronLeft, FiRefreshCw, FiUpload } from 'react-icons/fi';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import { feedsApi } from '../../api/feeds';
import { AccountBoundary, useRequestScope, useSettings } from '../../features/settings/shared';
import baseline from '../../app-ui/baseline';
import { shareOrCopyUrl } from '../../utils/share';
import { FeedAppPreviewLink, FeedPreviewHandoff } from './FeedAppPreview';
import { noticeDate } from '../../features/articles/model';
import FeedBody from './FeedBody';
import { feedCompactTitle } from './feedDetailModel';
import '../../features/articles/ArticleDetail.css';
import './FeedDetail.css';
export { feedDate } from './feedPresentation';

export default function FeedDetail() {
  const { identifier } = useParams();
  const { search } = useLocation();
  if (new URLSearchParams(search).get('previewInApp') === '1')
    return <FeedPreviewHandoff identifier={identifier} />;
  return <AccountBoundary component={FeedBoundary} />;
}
function FeedBoundary({ user }) {
  const { identifier } = useParams(),
    location = useLocation(),
    { locale } = useSettings();
  return (
    <FeedContent
      key={`${identifier}:${locale}:${location.key}`}
      {...{ user, identifier, location, locale }}
    />
  );
}
function FeedContent({ user, identifier, location, locale }) {
  const { style, en, dark } = useSettings(),
    scope = useRequestScope(user);
  const navigate = useNavigate(),
    client = useQueryClient();
  const [post, setPost] = useState(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(false);
  const [busy, setBusy] = useState(false),
    [sharing, setSharing] = useState(false),
    [message, setMessage] = useState('');
  const [actionError, setActionError] = useState(''),
    [expanded, setExpanded] = useState(false),
    [compact, setCompact] = useState(false);
  const lock = useRef(false),
    shareLock = useRef(false),
    read = useRef(null),
    scopeRef = useRef(scope);
  scopeRef.current = scope;
  const load = useCallback(async () => {
    if (lock.current) return;
    read.current?.cancel();
    const request = scopeRef.current();
    read.current = request;
    setLoading(true);
    setError(false);
    try {
      const item = await feedsApi.detail(
        identifier,
        !!user,
        request.options.signal,
        locale,
        request.options,
      );
      if (!item || item.id == null)
        throw Object.assign(Error('Unavailable'), { response: { status: 404 } });
      if (request.current()) setPost(item);
    } catch (failure) {
      if (request.current()) {
        setError(true);
        if ([401, 403, 404].includes(failure.response?.status)) setPost(null);
      }
    } finally {
      if (request.current()) setLoading(false);
      request.finish();
    }
  }, [identifier, locale, user]);
  useEffect(() => {
    void load();
    const visible = () => {
      if (!document.hidden) void load();
    };
    window.addEventListener('focus', visible);
    document.addEventListener('visibilitychange', visible);
    return () => {
      read.current?.cancel();
      window.removeEventListener('focus', visible);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [load]);
  useEffect(() => {
    const update = () =>
      setExpanded(parseFloat(getComputedStyle(document.documentElement).fontSize) >= 22.4);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    window.addEventListener('resize', update);
    update();
    let previous = window.scrollY;
    const scroll = () => {
      const offset = Math.max(0, window.scrollY);
      const before = previous;
      previous = offset;
      setCompact((c) => feedCompactTitle(c, offset, before));
    };
    window.addEventListener('scroll', scroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', scroll);
    };
  }, []);
  const login = () =>
    navigate(`/login?redirectUrl=${encodeURIComponent(location.pathname + location.search)}`);
  const save = async () => {
    if (!post || post.isHidden || lock.current) return;
    if (!user) return login();
    read.current?.cancel(); // A pending focus read must not overwrite a successful save.
    const request = scope();
    lock.current = true;
    setBusy(true);
    setLoading(false);
    setActionError('');
    setMessage('');
    try {
      await feedsApi.bookmark(post.id, post.isBookmarked, request.options);
      if (request.current()) {
        setPost((p) => ({ ...p, isBookmarked: !post.isBookmarked }));
        void client.invalidateQueries({
          predicate: (q) => ['feedDetail', 'me', 'myProfile', 'profile'].includes(q.queryKey[0]),
        });
      }
    } catch {
      if (request.current())
        setActionError(
          en ? 'Could not save. Please try again.' : '저장하지 못했습니다. 다시 시도해 주세요.',
        );
    } finally {
      if (request.current()) {
        lock.current = false;
        setBusy(false);
      }
      request.finish();
    }
  };
  const share = async () => {
    if (!post || post.isHidden || shareLock.current) return;
    const request = scope();
    shareLock.current = true;
    setSharing(true);
    setActionError('');
    setMessage('');
    try {
      const result = await shareOrCopyUrl({
        url: `/feed/${post.id}`,
        title: post.title,
        text: `${post.title} — ${en ? 'Postpop Feed' : '포스트팝 피드'}`,
      });
      if (request.current() && result === 'copied')
        setMessage(en ? 'Text and link copied.' : '공유 문구와 링크가 복사되었습니다.');
    } catch {
      if (request.current())
        setActionError(
          en ? 'Could not share. Please try again.' : '공유하지 못했습니다. 다시 시도해 주세요.',
        );
    } finally {
      if (request.current()) {
        shareLock.current = false;
        setSharing(false);
      }
      request.finish();
    }
  };
  const colors = baseline.tokens[dark ? 'darkColors' : 'lightColors'],
    title = en ? 'Postpop Feed' : '포스트팝 피드';
  return (
    <PageLayout paddingTop={{ xs: '16px', lg: '24px' }}>
      <section
        className={`pp-settings pp-article-detail pp-feed-detail${expanded ? ' pp-article-expanded' : ''}`}
        style={{
          ...style,
          '--article-body': colors.reviewBody,
          '--article-selected': colors.accentSelected,
        }}
      >
        <header className="pp-settings-header">
          <button
            aria-label={en ? 'Back' : '뒤로'}
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/feed'))}
          >
            <FiChevronLeft size={22} />
          </button>
          <h1 title={compact && post ? post.title : title}>
            {compact && post ? post.title : title}
          </h1>
          <button
            className="pp-article-refresh"
            aria-label={en ? 'Refresh' : '새로고침'}
            disabled={loading || busy}
            onClick={load}
          >
            <FiRefreshCw size={18} />
          </button>
        </header>
        {loading && !post && (
          <div
            className="pp-article-loading"
            role="status"
            aria-label={en ? 'Loading feed' : '게시물 불러오는 중'}
          >
            {Array.from({ length: 5 }, (_, i) => (
              <span key={i} />
            ))}
          </div>
        )}
        {error && (
          <div className="pp-article-state" role="alert">
            <p>{en ? 'We could not load this feed post.' : '피드 내용을 불러오지 못했습니다.'}</p>
            <button disabled={busy} onClick={load}>
              {en ? 'Try again' : '다시 시도'}
            </button>
          </div>
        )}
        {post && (
          <>
            {!post.isHidden && (
              <OpenInAppBanner type="feed" title={post.title} image={post.imageUrl} />
            )}
            {post.isHidden && (
              <>
                <Helmet>
                  <meta name="robots" content="noindex,nofollow" />
                </Helmet>
                <div className="pp-article-state" role="status">
                  <p>
                    {en
                      ? 'Hidden feed preview — only administrators can view this post.'
                      : '숨김 피드 미리보기 — 관리자만 볼 수 있으며 아직 공개되지 않았습니다.'}
                  </p>
                  <FeedAppPreviewLink identifier={String(post.id)} />
                </div>
              </>
            )}
            <SEO
              title={post.title}
              description={
                en
                  ? 'Discover the full story on Postpop Feed.'
                  : '포스트팝 피드에서 전체 이야기를 만나보세요.'
              }
              image={post.imageUrl}
              url={`${window.location.origin}/feed/${encodeURIComponent(post.slug || String(post.id))}`}
            />
            <article>
              <div className="pp-article-heading">
                <span className="pp-article-category">{title}</span>
                <h2>{post.title}</h2>
                <time className="pp-article-date">{noticeDate(post.createdAt, locale)}</time>
                {!post.isHidden && (
                  <div className="pp-article-actions">
                    <button aria-pressed={!!post.isBookmarked} disabled={busy} onClick={save}>
                      <FiBookmark
                        size={18}
                        fill={post.isBookmarked ? 'currentColor' : 'none'}
                        strokeWidth={1.8}
                      />
                      {post.isBookmarked ? (en ? 'Saved' : '저장됨') : en ? 'Save' : '저장'}
                    </button>
                    <button disabled={sharing} onClick={share}>
                      <FiUpload size={18} strokeWidth={1.8} />
                      {en ? 'Share' : '공유'}
                    </button>
                  </div>
                )}
              </div>
              {actionError && (
                <p className="pp-article-feedback" role="alert">
                  {actionError}
                </p>
              )}
              {message && (
                <p className="pp-article-feedback" role="status">
                  {message}
                </p>
              )}
              <div className="pp-feed-content">
                <FeedBody html={post.content} />
                {post.isContentLocked && <div className="pp-feed-gate-fade" aria-hidden="true" />}
              </div>
              {post.isContentLocked && (
                <div className="pp-feed-gate">
                  <h3>{en ? 'Keep reading the next part' : '다음 이야기도 이어서 읽어보세요'}</h3>
                  <p>
                    {en
                      ? 'Postpop members can read the full feed.'
                      : '포스트팝 회원은 피드 전체를 읽을 수 있어요.'}
                  </p>
                  <button onClick={login}>{en ? 'Log in or sign up' : '로그인·회원가입'}</button>
                </div>
              )}
            </article>
          </>
        )}
      </section>
    </PageLayout>
  );
}
